import { useState, useEffect, useRef } from 'react';
import { X, Search, Check, Loader2, AlertCircle, RotateCcw, Info, Save } from 'lucide-react';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { RankableItem } from './RankableItem';
import { useVoteStore } from '../../stores/voteStore';
import { subsetsApi } from '../../api/subsets';
import { songsApi } from '../../api/songs';
import { votesApi } from '../../api/votes';
import type { SubsetWithSongs, SongSummary } from '../../types';

interface VotingModalProps {
  subsetId: number;
  onClose: () => void;
  onVoteSubmitted?: () => void; // Optional callback when vote is successfully submitted
}

export function VotingModal({ subsetId, onClose, onVoteSubmitted }: VotingModalProps) {
  const [selectedSongIds, setSelectedSongIds] = useState<Set<number>>(new Set());
  const [rankedSongIds, setRankedSongIds] = useState<number[]>([]);
  const [specialCategoryVotes, setSpecialCategoryVotes] = useState<(number | null)[]>([null, null, null, null, null]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subset, setSubset] = useState<SubsetWithSongs | null>(null);
  const [songs, setSongs] = useState<SongSummary[]>([]);
  const [subsetChangedInfo, setSubsetChangedInfo] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'ranking' | 'special'>('ranking');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [hasExistingVote, setHasExistingVote] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);

  // Debounce timer for auto-save
  const autoSaveTimerRef = useRef<number | null>(null);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [subsetId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [subsetData, voteData, allSongs] = await Promise.all([
        subsetsApi.getSubsetById(subsetId),
        votesApi.getUserVote(subsetId),
        songsApi.getAllSongs(),
      ]);

      setSubset(subsetData);

      // Filter songs to only those in subset
      const subsetSongs = allSongs.filter(s => subsetData.songIds.includes(s.id));
      setSongs(subsetSongs);

      // Load existing vote if present
      if (voteData.hasVoted && voteData.vote) {
        setRankedSongIds(voteData.vote.rankedSongIds);
        setSelectedSongIds(new Set(voteData.vote.rankedSongIds));
        setSpecialCategoryVotes(voteData.vote.specialCategoryVotes || [null, null, null, null, null]);
        setIsSubmitted(voteData.vote.isSubmitted);
        setHasExistingVote(true);
      } else {
        setHasExistingVote(false);
        setIsSubmitted(false);
      }
    } catch (error: unknown) {
      const errorMessage = (error as { message?: string })?.message || 'Failed to load voting data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Auto-save draft when selections change (debounced)
  useEffect(() => {
    // Don't auto-save if loading, submitting, or if nothing selected yet
    if (loading || submitting || (!rankedSongIds.length && specialCategoryVotes.every(v => v === null))) {
      return;
    }

    // Don't auto-save drafts over an already-submitted vote unless user made changes
    if (isSubmitted && !hasUnsavedChanges) {
      return;
    }

    // Clear any existing timer
    if (autoSaveTimerRef.current) {
      window.clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer to save after 2 seconds of inactivity
    autoSaveTimerRef.current = window.setTimeout(async () => {
      // Double-check we're not in the middle of submitting (could have started during the 2s wait)
      if (submitting) return;
      setIsDraftSaving(true);
      await useVoteStore.getState().saveDraft(subsetId, rankedSongIds, specialCategoryVotes);
      setIsDraftSaving(false);
      setIsSubmitted(false); // Mark as draft when auto-saving
      setHasUnsavedChanges(false); // Clear unsaved flag after auto-save
    }, 2000);

    // Cleanup on unmount
    return () => {
      if (autoSaveTimerRef.current) {
        window.clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [rankedSongIds, specialCategoryVotes, subsetId, loading, submitting, isSubmitted, hasUnsavedChanges]);

  const handleToggleSong = (songId: number) => {
    const newSelected = new Set(selectedSongIds);

    if (newSelected.has(songId)) {
      // Remove from selection and ranking
      newSelected.delete(songId);
      setRankedSongIds(prev => prev.filter(id => id !== songId));
    } else {
      // Add to selection (if under limit)
      if (newSelected.size < (subset?.voteLimit || 12)) {
        newSelected.add(songId);
        setRankedSongIds(prev => [...prev, songId]);
      }
    }

    setSelectedSongIds(newSelected);
    setHasUnsavedChanges(true);
  };

  const handleRemoveFromRanking = (songId: number) => {
    const newSelected = new Set(selectedSongIds);
    newSelected.delete(songId);
    setSelectedSongIds(newSelected);
    setRankedSongIds(prev => prev.filter(id => id !== songId));
    setHasUnsavedChanges(true);
  };

  const handleClear = () => {
    setSelectedSongIds(new Set());
    setRankedSongIds([]);
    setSpecialCategoryVotes([null, null, null, null, null]);
    setError(null);
    setSubsetChangedInfo(null);
    setHasUnsavedChanges(true);
  };

  const handleReset = () => {
    setShowResetConfirmation(true);
  };

  const handleConfirmReset = async () => {
    try {
      setResetting(true);
      setError(null);
      await votesApi.deleteVote(subsetId);
      setHasUnsavedChanges(false);
      onVoteSubmitted?.(); // Notify parent to refresh
      onClose(); // Close modal
    } catch (error: unknown) {
      const errorMessage = (error as { message?: string })?.message || 'Failed to reset vote';
      setError(errorMessage);
    } finally {
      setResetting(false);
      setShowResetConfirmation(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setRankedSongIds((items) => {
        const oldIndex = items.findIndex(id => id === active.id);
        const newIndex = items.findIndex(id => id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      setHasUnsavedChanges(true);
    }
  };

  const handleSubmit = () => {
    if (!subset) {
      setError('Subset data not loaded');
      return;
    }
    if (rankedSongIds.length !== subset.voteLimit) {
      setError(`Please select exactly ${subset.voteLimit} songs for your main ranking (currently selected: ${rankedSongIds.length})`);
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmSubmit = async () => {
    if (!subset) return;

    // Validate that all selected song IDs are still in the subset
    const invalidIds = rankedSongIds.filter(id => !subset.songIds.includes(id));

    if (invalidIds.length > 0) {
      // Remove invalid IDs from selection
      const validIds = rankedSongIds.filter(id => subset.songIds.includes(id));
      setRankedSongIds(validIds);
      setSelectedSongIds(new Set(validIds));

      // Show info message
      const removedCount = invalidIds.length;
      const remainingCount = validIds.length;
      setSubsetChangedInfo(
        `The subset has changed. ${removedCount} song${removedCount > 1 ? 's' : ''} removed from your selection. ` +
        `You now have ${remainingCount} of ${subset.voteLimit} songs selected.`
      );
      setError(null);
      setShowConfirmation(false);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      // Cancel any pending auto-save timer to prevent it from overwriting the submit
      if (autoSaveTimerRef.current) {
        window.clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      await useVoteStore.getState().submitVote(subsetId, rankedSongIds, specialCategoryVotes);
      setIsSubmitted(true); // Mark as submitted
      setHasUnsavedChanges(false); // Clear unsaved changes flag
      onVoteSubmitted?.(); // Notify parent of successful vote submission
      onClose(); // Success - close modal
    } catch (error: unknown) {
      const errorMessage = (error as { message?: string })?.message || 'Failed to submit vote';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
      setShowConfirmation(false);
    }
  };

  const handleClose = async () => {
    // Clear any pending auto-save timer
    if (autoSaveTimerRef.current) {
      window.clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    // Immediately save draft if there are unsaved changes and vote is NOT already submitted
    // (avoid overwriting a submitted vote back to draft status)
    if (!isSubmitted && (rankedSongIds.length > 0 || specialCategoryVotes.some(v => v !== null))) {
      await useVoteStore.getState().saveDraft(subsetId, rankedSongIds, specialCategoryVotes);
      onVoteSubmitted?.(); // Refresh parent to show saved draft
    } else if (hasUnsavedChanges && !isSubmitted) {
      // If there are unsaved changes but no selections, just refresh parent
      onVoteSubmitted?.();
    }

    onClose();
  };

  const getFilteredSongs = (): SongSummary[] => {
    if (!searchQuery.trim()) return songs;

    const query = searchQuery.toLowerCase();
    return songs.filter(
      (song) =>
        song.title.toLowerCase().includes(query) ||
        song.artist.toLowerCase().includes(query)
    );
  };

  const getCategoryIcon = (categoryName: string): string => {
    const name = categoryName.toLowerCase();
    if (name.includes('story')) return '🎭';
    if (name.includes('heart')) return '💝';
    if (name.includes('fun')) return '🎉';
    if (name.includes('energy')) return '⚡';
    if (name.includes('cover')) return '🎨';
    return '⭐';
  };

  const getEnabledCategories = () => {
    return subset?.specialCategories.filter(cat => cat.enabled) || [];
  };

  const filteredSongs = getFilteredSongs();
  const rankedSongs = rankedSongIds.map(id => songs.find(s => s.id === id)).filter(Boolean) as SongSummary[];
  const enabledCategories = getEnabledCategories();

  if (loading) {
    return (
      <>
        <div
          className="fixed inset-0 bg-black/80 z-40 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-6xl md:max-h-[90vh] bg-monster-card border border-monster-border rounded-lg z-50 flex items-center justify-center">
          <Loader2 className="animate-spin" size={48} />
        </div>
      </>
    );
  }

  if (!subset) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80 z-40 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-6xl md:max-h-[90vh] bg-monster-card border border-monster-border rounded-lg z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-monster-border">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold">Cast Your Vote</h2>
              {/* Draft/Submitted Status Badge */}
              {hasExistingVote && (
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  isSubmitted
                    ? 'bg-green-600/20 text-green-400 border border-green-600/50'
                    : 'bg-blue-600/20 text-blue-400 border border-blue-600/50'
                }`}>
                  {isSubmitted ? 'Submitted' : 'Draft'}
                </span>
              )}
              {/* Auto-save Indicator */}
              {isDraftSaving && (
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <Save size={14} className="animate-pulse" />
                  Saving...
                </span>
              )}
            </div>
            <p className="text-sm text-gray-400 mt-1">
              Select {subset.voteLimit} favorites for "{subset.name}" and rank them
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-12 h-12 flex items-center justify-center rounded-full hover:bg-monster-dark transition-colors"
            aria-label="Close"
          >
            <X size={28} />
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-600/20 border border-red-600/50 rounded flex items-start gap-2 text-sm text-red-400">
            <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Subset Changed Info */}
        {subsetChangedInfo && (
          <div className="mx-4 mt-4 p-3 bg-blue-600/20 border border-blue-600/50 rounded flex items-start gap-2 text-sm text-blue-400">
            <Info size={18} className="flex-shrink-0 mt-0.5" />
            <span>{subsetChangedInfo}</span>
          </div>
        )}

        {/* Tabs */}
        {enabledCategories.length > 0 && (
          <div className="flex gap-2 px-4 pt-4 border-b border-monster-border">
            <button
              onClick={() => setActiveTab('ranking')}
              className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                activeTab === 'ranking'
                  ? 'border-monster-blue text-white'
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              Ranking
            </button>
            <button
              onClick={() => setActiveTab('special')}
              className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                activeTab === 'special'
                  ? 'border-monster-blue text-white'
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              Special Categories
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col md:flex-row gap-4 p-4">
          {/* Ranking Tab Content */}
          {activeTab === 'ranking' && (
            <>
              {/* Left Panel - Song Selection */}
              <div className="flex flex-col min-h-0 flex-1">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">Select Songs</h3>
                  <span className="text-sm text-gray-400">
                    {selectedSongIds.size} / {subset.voteLimit} selected
                  </span>
                </div>

                {/* Search */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search by title or artist..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-monster-dark border border-monster-border rounded focus:outline-none focus:border-monster-blue"
                  />
                </div>

                {/* Song Grid */}
                <div className="flex-1 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {filteredSongs.map((song) => {
                    const isSelected = selectedSongIds.has(song.id);
                    const isDisabled = !isSelected && selectedSongIds.size >= subset.voteLimit;

                    return (
                      <button
                        key={song.id}
                        onClick={() => handleToggleSong(song.id)}
                        disabled={isDisabled}
                        className={`flex items-center gap-3 p-2 rounded border transition-all text-left ${
                          isSelected
                            ? 'bg-monster-blue/20 border-monster-blue'
                            : isDisabled
                            ? 'bg-monster-dark/50 border-monster-border opacity-50 cursor-not-allowed'
                            : 'bg-monster-dark border-monster-border hover:border-monster-blue'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                          isSelected ? 'bg-monster-blue border-monster-blue' : 'border-gray-600'
                        }`}>
                          {isSelected && <Check size={16} />}
                        </div>
                        <img
                          src={song.imagePath}
                          alt={song.title}
                          className="w-10 h-10 rounded object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate text-sm">{song.title}</div>
                          <div className="text-xs text-gray-400 truncate">{song.artist}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Middle Panel - Ranking */}
              <div className="flex flex-col min-h-0 flex-1">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">Your Ranking</h3>
                  <span className="text-xs text-gray-400">Drag to reorder</span>
                </div>

                {/* Ranking List */}
                <div className="flex-1 overflow-y-auto space-y-2">
                  {rankedSongs.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                      Select songs to start ranking
                    </div>
                  ) : (
                    <DndContext onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
                      <SortableContext items={rankedSongIds} strategy={verticalListSortingStrategy}>
                        {rankedSongs.map((song, index) => (
                          <RankableItem
                            key={song.id}
                            song={song}
                            rank={index + 1}
                            points={subset.voteLimit - index}
                            onRemove={handleRemoveFromRanking}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Special Categories Tab Content */}
          {activeTab === 'special' && enabledCategories.length > 0 && (
            <div className="flex flex-col min-h-0 w-full max-w-2xl mx-auto">
              <div className="mb-3">
                <h3 className="font-semibold mb-2">Special Categories</h3>
                <p className="text-xs text-gray-400">
                  Optional - pick one song per category
                </p>
              </div>

              {/* Category Selection Dropdowns */}
              <div className="flex-1 overflow-y-auto space-y-4">
                {enabledCategories.map((category, categoryIndex) => {
                  // Find the actual index in the full array
                  const actualIndex = subset.specialCategories.findIndex(
                    (c, i) => c.enabled && subset.specialCategories.slice(0, i + 1).filter(cat => cat.enabled).length === categoryIndex + 1
                  );
                  const selectedSongId = specialCategoryVotes[actualIndex];

                  return (
                    <div key={actualIndex} className="p-3 bg-monster-dark rounded">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xl" title={category.name}>
                          {getCategoryIcon(category.name)}
                        </span>
                        <span className="text-sm font-medium">{category.name}</span>
                      </div>

                      {/* Dropdown for song selection */}
                      <select
                        value={selectedSongId || ''}
                        onChange={(e) => {
                          const newVotes = [...specialCategoryVotes];
                          newVotes[actualIndex] = e.target.value ? parseInt(e.target.value) : null;
                          setSpecialCategoryVotes(newVotes);
                          setHasUnsavedChanges(true);
                        }}
                        className="w-full px-3 py-2 bg-monster-card border border-monster-border rounded text-sm focus:outline-none focus:border-monster-blue"
                      >
                        <option value="">Not voted</option>
                        {songs.map(song => (
                          <option key={song.id} value={song.id}>
                            {song.title} - {song.artist}
                          </option>
                        ))}
                      </select>

                      {/* Clear button */}
                      {selectedSongId && (
                        <button
                          onClick={() => {
                            const newVotes = [...specialCategoryVotes];
                            newVotes[actualIndex] = null;
                            setSpecialCategoryVotes(newVotes);
                            setHasUnsavedChanges(true);
                          }}
                          className="mt-2 text-xs text-monster-blue hover:underline"
                        >
                          Clear selection
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-4 p-2 bg-monster-dark/50 rounded">
                <p className="text-xs text-gray-500 text-center">
                  🎭 Story  💝 Heart  🎉 Fun  ⚡ Energy  🎨 Cover
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-monster-border">
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded hover:bg-monster-dark transition-colors"
          >
            Cancel
          </button>
          <div className="flex gap-3">
            <button
              onClick={handleClear}
              disabled={rankedSongIds.length === 0 && specialCategoryVotes.every(v => v === null)}
              className={`px-4 py-2 rounded font-medium transition-all flex items-center gap-2 ${
                rankedSongIds.length > 0 || specialCategoryVotes.some(v => v !== null)
                  ? 'border border-monster-border hover:bg-monster-dark'
                  : 'border border-monster-border/50 text-gray-500 cursor-not-allowed opacity-50'
              }`}
            >
              Clear
            </button>
            <button
              onClick={handleReset}
              disabled={!hasExistingVote}
              className={`px-4 py-2 rounded font-medium transition-all flex items-center gap-2 ${
                hasExistingVote
                  ? 'border border-red-600 text-red-400 hover:bg-red-600/20'
                  : 'border border-monster-border/50 text-gray-500 cursor-not-allowed opacity-50'
              }`}
            >
              <RotateCcw size={16} />
              Reset Vote
            </button>
            <button
              onClick={handleSubmit}
              disabled={!subset || rankedSongIds.length !== subset.voteLimit}
              className={`px-6 py-2 rounded font-medium transition-all ${
                subset && rankedSongIds.length === subset.voteLimit
                  ? 'bg-monster-crimson text-white hover:bg-monster-crimson/80'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
              title={rankedSongIds.length !== subset?.voteLimit ? `Select exactly ${subset?.voteLimit} songs to submit` : undefined}
            >
              Submit Vote
            </button>
          </div>
        </div>
      </div>

      {/* Submit Confirmation Dialog */}
      {showConfirmation && (
        <>
          <div className="fixed inset-0 bg-black/90 z-[60]" onClick={() => setShowConfirmation(false)} />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-monster-card border border-monster-border rounded-lg z-[70] p-6">
            <h3 className="text-xl font-bold mb-2">Confirm Your Vote</h3>
            <p className="text-gray-400 mb-4">
              You're about to submit your vote for "{subset.name}". You can change it later until voting closes.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmation(false)}
                disabled={submitting}
                className="flex-1 px-4 py-2 rounded border border-monster-border hover:bg-monster-dark transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSubmit}
                disabled={submitting}
                className="flex-1 px-4 py-2 rounded bg-monster-crimson text-white hover:bg-monster-crimson/80 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Submitting...
                  </>
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Reset Confirmation Dialog */}
      {showResetConfirmation && (
        <>
          <div className="fixed inset-0 bg-black/90 z-[60]" onClick={() => setShowResetConfirmation(false)} />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-monster-card border border-red-600 rounded-lg z-[70] p-6">
            <h3 className="text-xl font-bold mb-2 text-red-400">Reset Your Vote?</h3>
            <p className="text-gray-400 mb-4">
              Are you sure you want to completely reset your vote for "{subset.name}"? This will remove all your selections from the server. You can vote again later.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowResetConfirmation(false)}
                disabled={resetting}
                className="flex-1 px-4 py-2 rounded border border-monster-border hover:bg-monster-dark transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReset}
                disabled={resetting}
                className="flex-1 px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {resetting ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Resetting...
                  </>
                ) : (
                  'Yes, Reset'
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
